import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';

import { SnackbarService } from '../../../core/services';
import { UserGetProfilePhotoFailure, UserGetProfilePhotoRequest, UserGetProfilePhotoSuccess, UserGetRequest, UserRemoveProfilePhotoFailure, UserRemoveProfilePhotoRequest, UserRemoveProfilePhotoSuccess, UserUpdateFailure, UserUpdateRequest, UserUpdateSuccess } from '../../../store/user/user.actions';
import { normalizeUserAttributes, User } from '../../../store/user/user.models';
import { selectUser } from '../../../store/user/user.selectors';
import { environment } from '../../../../environments/environment';
import { Actions, ofType } from '@ngrx/effects';
import { take } from 'rxjs';

@Component({
  selector: 'app-profile',
  imports: [ReactiveFormsModule, RouterLink, MatCardModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, MatSelectModule],
  templateUrl: './profile.html',
  styleUrl: './profile.scss',
})
export class Profile {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackbar = inject(SnackbarService);
  private readonly actions$ = inject(Actions);
  private readonly browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  private objectPhotoPreviewUrl: string | null = null;

  protected readonly photoPreview = signal<string | null>(null);
  protected readonly selectedPhoto = signal<File | null>(null);
  protected readonly removeSavedPhotoPending = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly userInfo = signal<User | null>(null);
  protected readonly timeZoneOptions = signal<string[]>(this.buildTimeZoneOptions());

  protected readonly profileForm = this.fb.nonNullable.group({
    fullName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    phone: [''],
    timezone: [''],
  });

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.revokeObjectPhotoPreview());

    this.store.dispatch(UserGetRequest());

    this.store
      .select(selectUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.userInfo.set(user);

        const resolvedTimeZone = user?.timezone?.trim() || this.browserTimeZone;
        this.timeZoneOptions.set(this.buildTimeZoneOptions(resolvedTimeZone));

        if (!this.selectedPhoto() && !this.removeSavedPhotoPending()) {
          const storedPhotoUrl = this.getStoredProfilePhotoUrl(user);
          if (storedPhotoUrl) {
            this.revokeObjectPhotoPreview();
            this.photoPreview.set(storedPhotoUrl);
          } else if (this.hasStoredProfilePhoto(user)) {
            this.loadStoredProfilePhoto();
          } else {
            this.revokeObjectPhotoPreview();
            this.photoPreview.set(null);
          }
        }

        if (!user) {
          this.profileForm.patchValue({ timezone: this.browserTimeZone });
          return;
        }

        this.profileForm.patchValue({
          fullName: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          timezone: resolvedTimeZone,
        });
      });
  }

  private buildTimeZoneOptions(preferredTimeZone?: string): string[] {
    const preferred = preferredTimeZone?.trim() || this.browserTimeZone;
    const supportedTimeZones = typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('timeZone')
      : [];

    const fallbackTimeZones = [
      'UTC',
      'Asia/Kolkata',
      'Europe/London',
      'America/New_York',
      'America/Los_Angeles',
      'Australia/Sydney',
      'Asia/Singapore',
      'Asia/Dubai',
    ];

    return Array.from(new Set([preferred, this.browserTimeZone, ...supportedTimeZones, ...fallbackTimeZones]));
  }

  protected getProfileInitials(): string {
    const name = this.userInfo()?.name?.trim();
    if (!name) {
      return 'U';
    }

    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  protected openPhotoPicker(input: HTMLInputElement): void {
    input.value = '';
    input.click();
  }

  protected onPhotoSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        this.photoPreview.set(result);
      }
    };
    this.removeSavedPhotoPending.set(false);
    this.selectedPhoto.set(file);
    reader.readAsDataURL(file);
  }

  protected removeProfilePhoto(): void {
    if (this.isSaving()) {
      return;
    }

    if (this.selectedPhoto()) {
      this.selectedPhoto.set(null);
      this.restoreStoredPhotoPreview(this.userInfo());
      return;
    }

    if (!this.hasStoredProfilePhoto(this.userInfo())) {
      this.photoPreview.set(null);
      return;
    }

    this.isSaving.set(true);
    this.removeSavedPhotoPending.set(true);
    this.store.dispatch(UserRemoveProfilePhotoRequest());

    this.actions$.pipe(ofType(UserRemoveProfilePhotoSuccess), take(1)).subscribe((response) => {
      const updatedUser = response?.user as User | undefined;
      this.selectedPhoto.set(null);
      this.removeSavedPhotoPending.set(false);
      this.revokeObjectPhotoPreview();
      this.photoPreview.set(null);

      if (updatedUser) {
        this.userInfo.set(updatedUser);
      }

      this.store.dispatch(UserGetRequest());
      this.isSaving.set(false);
    });

    this.actions$.pipe(ofType(UserRemoveProfilePhotoFailure), take(1)).subscribe(() => {
      this.removeSavedPhotoPending.set(false);
      this.isSaving.set(false);
      this.restoreStoredPhotoPreview(this.userInfo());
    });
  }

  protected saveProfile(): void {
    if (this.profileForm.invalid || this.isSaving()) {
      this.profileForm.markAllAsTouched();
      return;
    }

    const payload = this.profileForm.getRawValue();
    const formData = new FormData();

    formData.append('fullName', payload.fullName);
    formData.append('email', payload.email);
    formData.append('phone', payload.phone ?? '');
    formData.append('timezone', payload.timezone ?? '');

    const selectedPhoto = this.selectedPhoto();
    if (selectedPhoto) {
      formData.append('photo', selectedPhoto);
    }

    this.isSaving.set(true);

    this.store.dispatch(UserUpdateRequest({ payload: formData }));

    this.actions$.pipe(ofType(UserUpdateSuccess), take(1)).subscribe((response) => {
          const updatedUser = response?.user as User | undefined;
          this.selectedPhoto.set(null);
          this.removeSavedPhotoPending.set(false);

          if (updatedUser) {
            this.userInfo.set(updatedUser);
            const storedPhotoUrl = this.getStoredProfilePhotoUrl(updatedUser);
            if (storedPhotoUrl) {
              this.revokeObjectPhotoPreview();
              this.photoPreview.set(storedPhotoUrl);
            } else if (this.hasStoredProfilePhoto(updatedUser)) {
              this.loadStoredProfilePhoto();
            }
          }

          this.store.dispatch(UserGetRequest());
          this.snackbar.showNotification('success', '', 'Profile updated successfully', 'Close');
          this.isSaving.set(false);
    });

    this.actions$.pipe(ofType(UserUpdateFailure), take(1)).subscribe(() => {
          this.removeSavedPhotoPending.set(false);
          this.isSaving.set(false);
          this.restoreStoredPhotoPreview(this.userInfo());
    });
  }

  protected removePhotoButtonLabel(): string {
    return this.selectedPhoto() ? 'Remove selected photo' : 'Remove profile photo';
  }

  protected canRemovePhoto(): boolean {
    return Boolean(this.selectedPhoto() || this.photoPreview() || this.hasStoredProfilePhoto(this.userInfo()));
  }

  private getStoredProfilePhotoUrl(user: User | null | undefined): string | null {
    const url = normalizeUserAttributes(user?.attributes ?? null)['profile_photo_url'];
    return this.toRenderablePhotoUrl(url);
  }

  private hasStoredProfilePhoto(user: User | null | undefined): boolean {
    const key = normalizeUserAttributes(user?.attributes ?? null)['profile_photo_key'];
    return typeof key === 'string' && key.trim().length > 0;
  }

  private loadStoredProfilePhoto(): void {
    this.store.dispatch(UserGetProfilePhotoRequest());
    this.actions$.pipe(ofType(UserGetProfilePhotoSuccess), take(1)).subscribe((blob) => {
      this.revokeObjectPhotoPreview();
      this.objectPhotoPreviewUrl = URL.createObjectURL(blob.blob);
      this.photoPreview.set(this.objectPhotoPreviewUrl);
    });

    this.actions$.pipe(ofType(UserGetProfilePhotoFailure), take(1)).subscribe(() => {
      this.revokeObjectPhotoPreview();
      this.photoPreview.set(null);
    });
  }

  private restoreStoredPhotoPreview(user: User | null): void {
    const storedPhotoUrl = this.getStoredProfilePhotoUrl(user);
    if (storedPhotoUrl) {
      this.revokeObjectPhotoPreview();
      this.photoPreview.set(storedPhotoUrl);
      return;
    }

    if (this.hasStoredProfilePhoto(user)) {
      this.loadStoredProfilePhoto();
      return;
    }

    this.revokeObjectPhotoPreview();
    this.photoPreview.set(null);
  }

  private revokeObjectPhotoPreview(): void {
    if (this.objectPhotoPreviewUrl) {
      URL.revokeObjectURL(this.objectPhotoPreviewUrl);
      this.objectPhotoPreviewUrl = null;
    }
  }


  private toRenderablePhotoUrl(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (/^(https?:|blob:|data:)/i.test(trimmed)) {
      if (/^(blob:|data:)/i.test(trimmed)) {
        return trimmed;
      }

      try {
        const candidateUrl = new URL(trimmed);
        const apiBaseUrl = new URL(environment.apiBaseUrl);
        if (candidateUrl.origin === apiBaseUrl.origin) {
          return trimmed;
        }
      } catch {
        return null;
      }

      return null;
    }

    if (trimmed.startsWith('/')) {
      return new URL(trimmed, environment.apiBaseUrl).toString();
    }

    return null;
  }
}
